/**
 * Order slice - handles order-related state and operations
 * Maps to OrderController endpoints
 */

import { PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { companyChanged } from 'store/actions/companyActions';
import type { ThunkConf } from 'store/thunks';
import type { RootState } from 'store';
import axios from 'axios';
import { orderService } from 'services/orderService';
import { creditNoteService, CreditNoteDTO } from 'services/creditNoteService';
import {
    OrderApiOrderGetOrderRequest,
    ReturnOrderHeader,
    OrderForReturnDTO,
    OrderDeliveryInfo,
    CustomerSearchItem,
    CustomerSearchItemPageInfo,
    BarcodeSearchResult,
} from 'returnCenter-api';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import { addOrderToReturnSlip, populateMissingLpns } from './returnSlice';
import { setDecisionCodes, setRuleSetName } from './decisionCodeSlice';
import { skuLocationThunks } from './skuLocationSlice';
import { getDateMonthsAgo } from 'utils/dates';
import { configSelectors } from './configSlice';

export interface OrderSearchFilters {
    query: string;
    from?: Date;
    to?: Date;
}

interface OrderState {
    // Order state
    orderLoading: boolean;
    errorOnLoading: boolean;
    errorMessage: string | null;

    // Search orders state
    searchOrders: ReturnOrderHeader[] | null;
    searchLoading: boolean;
    selectedOrder: ReturnOrderHeader | null;
    orderSearchFilters: OrderSearchFilters;

    // Order delivery info
    orderDeliveryInfo: OrderDeliveryInfo[];
    orderDeliveryInfoLoading: boolean;

    // Credit note order (for modal display)
    creditNoteOrder: CreditNoteDTO | null;
    creditNoteOrderLoading: boolean;
    creditNoteOrderError: string | null;

    // Customer search state
    customerSearchResults: CustomerSearchItem[];
    customerSearchLoading: boolean;
    customerSearchError: string | null;

    // Barcode lookup state
    barcodeLookupLoading: boolean;
    barcodeLookupError: string | null;
}

const initialState: OrderState = {
    orderLoading: false,
    errorOnLoading: false,
    errorMessage: null,

    searchOrders: null,
    searchLoading: false,
    selectedOrder: null,
    orderSearchFilters: { query: '', from: getDateMonthsAgo(6), to: new Date() },
    orderDeliveryInfo: [],
    orderDeliveryInfoLoading: false,

    creditNoteOrder: null,
    creditNoteOrderLoading: false,
    creditNoteOrderError: null,

    customerSearchResults: [],
    customerSearchLoading: false,
    customerSearchError: null,

    barcodeLookupLoading: false,
    barcodeLookupError: null,
};

// ============ THUNKS ============

export const searchOrders = createAsyncThunk<
    ReturnOrderHeader[],
    { searchString: string; customerCode?: string | null; searchArticles?: boolean; from?: string; to?: string },
    ThunkConf
>('order/search', async ({ searchString, customerCode, searchArticles, from, to }, { signal }) => {
    if (!searchString.trim()) {
        toast.warn(t('pleaseEnterValidSearchId'), {
            autoClose: 3500,
            closeOnClick: true,
        });
    }

    return await orderService.getOrders(
        {
            search: searchString,
            customerCode: customerCode ?? undefined,
            searchArticles: searchArticles ?? false,
            from: from ?? undefined,
            to: to ?? undefined,
        },
        signal,
    );
});

export const getOrder = createAsyncThunk<OrderForReturnDTO, OrderApiOrderGetOrderRequest, ThunkConf>(
    'order/get',
    async ({ orderId, customerCode }, { dispatch, rejectWithValue }) => {
        try {
            const result = await orderService.getOrder({
                orderId: orderId,
                customerCode: customerCode ?? undefined,
            });

            // Fetch delivery info for this single order
            dispatch(getSingleOrderDeliveryInfo([String(orderId)]));

            return result;
        } catch (error) {
            console.error('Error in getOrder', error);
            if (axios.isCancel(error)) {
                return rejectWithValue(undefined);
            }
            toast.error(t('errorLoadingOrder'), {
                autoClose: 3500,
                closeOnClick: true,
            });
            return rejectWithValue(error);
        }
    },
);

interface GetOrderAndAddToReturnSlipParams {
    orderId: string;
    customerCode?: string;
    /** True when adding an additional order to an existing return slip */
    isAddingToExisting?: boolean;
    /** True when all items should be pre-filled with full available quantity (e.g. unclaimed parcel via barcode scan) */
    fillMaxReturnQty?: boolean;
    /** Search mode - determines if loading spinner should be shown (barcode mode = no spinner) */
    searchMode?: 'orderNumber' | 'articleNumber' | 'barcode';
}

/**
 * Compound thunk: fetches order and adds it to return slip with decision codes.
 * Use this when creating/editing a return slip from an order.
 * Use getOrder directly when you only need the order data without return slip operations.
 *
 * Dispatches to other slices to initialize required data:
 * - returnSlice: addOrderToReturnSlip
 * - decisionCodeSlice: setDecisionCodes, setRuleSetName
 * - skuLocationSlice: fetchSkuLocations
 */
export const getOrderAndAddToReturnSlip = createAsyncThunk<
    OrderForReturnDTO,
    GetOrderAndAddToReturnSlipParams,
    ThunkConf
>(
    'order/getAndAddToReturnSlip',
    async (
        { orderId, customerCode, isAddingToExisting, fillMaxReturnQty },
        { dispatch, getState, rejectWithValue },
    ) => {
        const marketId = getState().companies.selectedMarketId;
        const arrivalDate = getState().returns.returnSlip?.arrivalDate?.utc ?? new Date().toISOString();
        try {
            const order = await orderService.getOrder({
                orderId,
                customerCode: customerCode ?? undefined,
                arrivalDate,
            });

            // Add order to return slip (fillAllItems pre-fills quantities for unclaimed parcels)
            dispatch(
                addOrderToReturnSlip({
                    order,
                    fillMaxReturnQty: fillMaxReturnQty ?? false,
                    marketId,
                }),
            );

            if (configSelectors.selectIsM3Company(getState())) {
                dispatch(populateMissingLpns());
            }

            // Every SKU on the slip, not just this order's: skuLocationSlice replaces its
            // map on each fetch, so a partial list would drop other orders' locations.
            const skus = [
                ...new Set(
                    (getState().returns.returnSlip?.returnItems ?? [])
                        .flatMap((group) => group.orderLines ?? [])
                        .map((line) => line.sku)
                        .filter((sku): sku is string => !!sku),
                ),
            ];
            if (skus.length > 0) {
                dispatch(skuLocationThunks.fetchSkuLocations(skus));
            }

            // Set decision codes (skip if adding to existing return slip)
            if (order.decisionCodes && !isAddingToExisting) {
                dispatch(setDecisionCodes(order.decisionCodes.decisionCodes ?? null));
                dispatch(setRuleSetName(order.decisionCodes.ruleSetName ?? null));
            }

            // Fetch delivery info for this order
            dispatch(getSingleOrderDeliveryInfo([String(orderId)]));

            return order;
        } catch (error) {
            console.error('Error in getOrderAndAddToReturnSlip', error);
            if (axios.isCancel(error)) {
                return rejectWithValue(undefined);
            }
            toast.error(t('errorLoadingOrder'), {
                autoClose: 3500,
                closeOnClick: true,
            });
            return rejectWithValue(error);
        }
    },
);

export const getOrderByBarcode = createAsyncThunk<BarcodeSearchResult, string, ThunkConf>(
    'order/getByBarcode',
    async (barcode, { rejectWithValue }) => {
        try {
            return await orderService.getOrderByBarcode({ barcode });
        } catch (error) {
            console.error('Error in getOrderByBarcode', error);
            if (axios.isCancel(error)) {
                return rejectWithValue({ message: 'Request cancelled' });
            }
            return rejectWithValue({ message: error instanceof Error ? error.toString() : 'Unknown error' });
        }
    },
);

export const getOrderByBarcodeForCustomer = createAsyncThunk<
    BarcodeSearchResult,
    { barcode: string; customerCode: string },
    ThunkConf
>('order/getByBarcodeForCustomer', async ({ barcode, customerCode }, { rejectWithValue }) => {
    try {
        return await orderService.getOrderByBarcode({ barcode, customerCode });
    } catch (error) {
        console.error('Error in getOrderByBarcode', error);
        if (axios.isCancel(error)) {
            return rejectWithValue({ message: 'Request cancelled' });
        }
        return rejectWithValue({ message: error instanceof Error ? error.toString() : 'Unknown error' });
    }
});

export const getOrderDeliveryInfo = createAsyncThunk<OrderDeliveryInfo[], string[], ThunkConf>(
    'order/deliveryInfo/get',
    async (orderNumbers, { rejectWithValue }) => {
        try {
            return await orderService.getOrderDeliveryInfo({ requestBody: orderNumbers });
        } catch (error) {
            console.error('Error fetching order delivery info:', error);
            if (axios.isCancel(error)) {
                return rejectWithValue(undefined);
            }
            return rejectWithValue({ message: error instanceof Error ? error.toString() : 'Unknown error' });
        }
    },
);

export const getSingleOrderDeliveryInfo = createAsyncThunk<OrderDeliveryInfo[], string[], ThunkConf>(
    'order/deliveryInfo/getSingle',
    async (orderNumbers, { rejectWithValue }) => {
        try {
            return await orderService.getOrderDeliveryInfo({ requestBody: orderNumbers });
        } catch (error) {
            console.error('Error fetching single order delivery info:', error);
            if (axios.isCancel(error)) {
                return rejectWithValue(undefined);
            }
            return rejectWithValue({ message: error instanceof Error ? error.toString() : 'Unknown error' });
        }
    },
);

/**
 * Fetch order data for credit note modal display.
 * Uses separate state to avoid interfering with main order flow.
 */
export const getCreditNoteOrder = createAsyncThunk<CreditNoteDTO, string, ThunkConf>(
    'order/getCreditNoteOrder',
    async (orderNumber, { rejectWithValue }) => {
        try {
            return await creditNoteService.getCreditNote(orderNumber);
        } catch (error) {
            console.error('Error fetching credit note order:', error);
            if (axios.isCancel(error)) {
                return rejectWithValue(undefined);
            }
            return rejectWithValue({ message: error instanceof Error ? error.toString() : 'Unknown error' });
        }
    },
);

/**
 * Search customers by name, code or group.
 */
export const searchCustomers = createAsyncThunk<
    CustomerSearchItemPageInfo,
    { search: string; pageSize?: number; pageNumber?: number },
    ThunkConf
>('order/searchCustomers', async ({ search, pageSize, pageNumber }, { rejectWithValue, signal }) => {
    return await orderService.searchCustomers(
        { search, pageSize: pageSize ?? 20, pageNumber: pageNumber ?? 1 },
        signal,
    );
});

// ============ SLICE ============

const orderSlice = createSlice({
    name: 'order',
    initialState,
    reducers: {
        resetOrderState: () => initialState,

        clearSearchOrders(state) {
            state.searchOrders = null;
            state.searchLoading = false;
            state.errorMessage = null;
        },

        clearErrors(state) {
            state.errorOnLoading = false;
            state.errorMessage = null;
        },

        selectedOrderSet(state, action: PayloadAction<ReturnOrderHeader>) {
            state.selectedOrder = action.payload;
        },

        clearOrderDeliveryInfo(state) {
            state.orderDeliveryInfo = [];
        },

        removeOrderDeliveryInfo(state, action: PayloadAction<string[]>) {
            const orderNumbersToKeep = action.payload;
            state.orderDeliveryInfo = state.orderDeliveryInfo.filter(
                (info) => info.orderNumber && orderNumbersToKeep.includes(info.orderNumber),
            );
        },

        clearCreditNoteOrder(state) {
            state.creditNoteOrder = null;
            state.creditNoteOrderError = null;
        },

        clearCustomerSearch(state) {
            state.customerSearchResults = [];
            state.customerSearchLoading = false;
            state.customerSearchError = null;
        },

        setOrderSearchFilters(state, action: PayloadAction<Partial<OrderSearchFilters>>) {
            state.orderSearchFilters = { ...state.orderSearchFilters, ...action.payload };
        },
        clearOrderSearchFilters(state) {
            state.orderSearchFilters = { query: '', from: getDateMonthsAgo(6), to: new Date() };
        },
    },
    extraReducers: (builder) => {
        // searchOrders
        builder
            .addCase(searchOrders.pending, (state) => {
                state.searchLoading = true;
                state.errorMessage = null;
            })
            .addCase(searchOrders.fulfilled, (state, action) => {
                state.searchOrders = action.payload;
                state.searchLoading = false;
                state.errorMessage = null;
            })
            .addCase(searchOrders.rejected, (state, action) => {
                if (action.meta?.aborted) return;
                state.searchLoading = false;
                state.errorMessage = action.error?.message ?? 'Unknown error';
            });

        // getOrder
        builder
            .addCase(getOrder.pending, (state) => {
                state.orderLoading = true;
            })
            .addCase(getOrder.fulfilled, (state) => {
                state.orderLoading = false;
                state.errorOnLoading = false;
            })
            .addCase(getOrder.rejected, (state, action) => {
                state.orderLoading = false;
                state.errorOnLoading = true;
                state.errorMessage = (action.payload as any)?.message ?? 'Unknown error';
            });

        // getOrderAndAddToReturnSlip
        // Two scenarios:
        // 1. Barcode mode (searchMode === 'barcode'): no loading spinner, modal stays open
        // 2. Order/Article mode: show loading spinner, modal can close after addition
        builder
            .addCase(getOrderAndAddToReturnSlip.pending, (state, action) => {
                // Only show loading spinner in non-barcode modes
                if (action.meta.arg.searchMode !== 'barcode') {
                    state.orderLoading = true;
                }
                if (action.meta.arg.searchMode === 'barcode') {
                    state.barcodeLookupLoading = true;
                    state.barcodeLookupError = null;
                }

                state.errorOnLoading = false;
                state.errorMessage = null;
                // Clear delivery info when creating a new return slip
                if (!action.meta.arg.isAddingToExisting) {
                    state.orderDeliveryInfo = [];
                }
            })
            .addCase(getOrderAndAddToReturnSlip.fulfilled, (state, action) => {
                // Only unset loading for non-barcode modes
                if (action.meta.arg.searchMode !== 'barcode') {
                    state.orderLoading = false;
                }
                if (action.meta.arg.searchMode === 'barcode') {
                    state.barcodeLookupLoading = false;
                    state.barcodeLookupError = null;
                }
                state.errorOnLoading = false;
            })
            .addCase(getOrderAndAddToReturnSlip.rejected, (state, action) => {
                // Always unset loading on error, but still capture error state
                if (action.meta.arg.searchMode !== 'barcode') {
                    state.orderLoading = false;
                }
                if (action.meta.arg.searchMode === 'barcode') {
                    state.barcodeLookupLoading = false;
                    state.barcodeLookupError = (action.payload as any)?.message ?? 'Unknown error';
                }
                state.errorOnLoading = true;
                state.errorMessage = (action.payload as any)?.message ?? 'Unknown error';
            });

        // getOrderByBarcode
        builder
            .addCase(getOrderByBarcode.pending, (state) => {
                state.orderLoading = true;
                state.errorOnLoading = false;
                state.errorMessage = null;
            })
            .addCase(getOrderByBarcode.fulfilled, (state) => {
                state.orderLoading = false;
                state.errorOnLoading = false;
            })
            .addCase(getOrderByBarcode.rejected, (state, action) => {
                state.orderLoading = false;
                state.errorOnLoading = true;
                state.errorMessage = (action.payload as any)?.message ?? 'Unknown error';
            });

        // getOrderByBarcodeForCustomer
        builder
            .addCase(getOrderByBarcodeForCustomer.pending, (state) => {
                state.barcodeLookupLoading = true;
                state.barcodeLookupError = null;
            })
            .addCase(getOrderByBarcodeForCustomer.fulfilled, (state) => {
                state.barcodeLookupLoading = false;
                state.barcodeLookupError = null;
            })
            .addCase(getOrderByBarcodeForCustomer.rejected, (state, action) => {
                state.barcodeLookupLoading = false;
                state.barcodeLookupError = (action.payload as any)?.message ?? 'Unknown error';
            });

        // getOrderDeliveryInfo
        builder
            .addCase(getOrderDeliveryInfo.pending, (state) => {
                state.orderDeliveryInfoLoading = true;
            })
            .addCase(getOrderDeliveryInfo.fulfilled, (state, action) => {
                state.orderDeliveryInfo = action.payload ?? [];
                state.orderDeliveryInfoLoading = false;
            })
            .addCase(getOrderDeliveryInfo.rejected, (state) => {
                state.orderDeliveryInfoLoading = false;
            });

        // getSingleOrderDeliveryInfo
        builder.addCase(getSingleOrderDeliveryInfo.fulfilled, (state, action) => {
            const newInfos = action.payload ?? [];
            const newOrderNumbers = newInfos.map((info) => info.orderNumber);
            // Filter out existing entries for the same order numbers (replace strategy)
            const existingInfos = state.orderDeliveryInfo.filter((info) => !newOrderNumbers.includes(info.orderNumber));
            state.orderDeliveryInfo = [...newInfos, ...existingInfos];
        });

        // getCreditNoteOrder
        builder
            .addCase(getCreditNoteOrder.pending, (state) => {
                state.creditNoteOrderLoading = true;
                state.creditNoteOrderError = null;
            })
            .addCase(getCreditNoteOrder.fulfilled, (state, action) => {
                state.creditNoteOrder = action.payload;
                state.creditNoteOrderLoading = false;
            })
            .addCase(getCreditNoteOrder.rejected, (state, action) => {
                state.creditNoteOrderLoading = false;
                state.creditNoteOrderError = (action.payload as any)?.message ?? 'Unknown error';
            });

        // Reset on company change
        builder.addCase(companyChanged, () => initialState);

        // searchCustomers
        builder
            .addCase(searchCustomers.pending, (state) => {
                state.customerSearchLoading = true;
                state.customerSearchError = null;
            })
            .addCase(searchCustomers.fulfilled, (state, action) => {
                state.customerSearchResults = action.payload.items ?? [];
                state.customerSearchLoading = false;
            })
            .addCase(searchCustomers.rejected, (state, action) => {
                if (action.meta?.aborted) return;
                state.customerSearchLoading = false;
                state.customerSearchError = action.error?.message ?? 'Unknown error';
            });
    },
});

// ============ SELECTORS ============

export const selectOrderLoading = (state: RootState) => state.orders.orderLoading;
export const selectSearchLoading = (state: RootState) => state.orders.searchLoading;
export const selectOrderErrorOnLoading = (state: RootState) => state.orders.errorOnLoading;
export const selectErrorMessage = (state: RootState) => state.orders.errorMessage;
export const selectOrderHeaders = (state: RootState) => state.orders.searchOrders;
export const selectSelectedOrder = (state: RootState) => state.orders.selectedOrder;
export const selectOrderDeliveryInfo = (state: RootState) => state.orders.orderDeliveryInfo;
export const selectOrderDeliveryInfoLoading = (state: RootState) => state.orders.orderDeliveryInfoLoading;
export const selectCreditNoteOrder = (state: RootState) => state.orders.creditNoteOrder;
export const selectCreditNoteOrderLoading = (state: RootState) => state.orders.creditNoteOrderLoading;
export const selectCreditNoteOrderError = (state: RootState) => state.orders.creditNoteOrderError;
export const selectCustomerSearchResults = (state: RootState) => state.orders.customerSearchResults;
export const selectCustomerSearchLoading = (state: RootState) => state.orders.customerSearchLoading;
export const selectCustomerSearchError = (state: RootState) => state.orders.customerSearchError;
export const selectBarcodeLookupLoading = (state: RootState) => state.orders.barcodeLookupLoading;
export const selectBarcodeLookupError = (state: RootState) => state.orders.barcodeLookupError;
export const selectOrderSearchQuery = (state: RootState) => state.orders.orderSearchFilters.query;
export const selectOrderSearchFilters = (state: RootState) => state.orders.orderSearchFilters;

export const selectedOrderCustomerCode = createSelector(
    selectSelectedOrder,
    (selectedOrder) => selectedOrder?.customerCode,
);

// ============ EXPORTS ============

export const orderActions = orderSlice.actions;
export const {
    resetOrderState,
    clearSearchOrders,
    clearErrors,
    selectedOrderSet,
    clearOrderDeliveryInfo,
    removeOrderDeliveryInfo,
    clearCreditNoteOrder,
    clearCustomerSearch,
    setOrderSearchFilters,
    clearOrderSearchFilters,
} = orderSlice.actions;

export const orderThunks = {
    searchOrders,
    getOrder,
    getOrderAndAddToReturnSlip,
    getOrderByBarcode,
    getOrderByBarcodeForCustomer,
    getOrderDeliveryInfo,
    getSingleOrderDeliveryInfo,
    getCreditNoteOrder,
    searchCustomers,
};

export const orderSelectors = {
    selectOrderLoading,
    selectSearchLoading,
    selectOrderErrorOnLoading,
    selectErrorMessage,
    selectOrderHeaders,
    selectSelectedOrder,
    selectOrderDeliveryInfo,
    selectOrderDeliveryInfoLoading,
    selectedOrderCustomerCode,
    selectCreditNoteOrder,
    selectCreditNoteOrderLoading,
    selectCreditNoteOrderError,
    selectCustomerSearchResults,
    selectCustomerSearchLoading,
    selectCustomerSearchError,
    selectBarcodeLookupLoading,
    selectBarcodeLookupError,
    selectOrderSearchQuery,
    selectOrderSearchFilters,
};

export default orderSlice.reducer;
